import React, { useState } from 'react';
import { Alert, Box, Grid, Stack, TextField } from '@mui/material';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { UserAdminDto } from '../../../../dtos/Users/UserAdminDto';
import UserApiService from '../../../../api/UserApiService';
import SaveButton from '../../../Common/SaveButton';
import Section from '../../../Common/Section';
import { useAsyncCallback } from '../../../../hooks/useAsyncCallback';
import { useSnackbar } from '../../../Common/SnackbarProvider';

export default function UserChangePassword (props) {
    const { user } = props;
    const { t } = useTranslation();
    const snackbar = useSnackbar();
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [changePasswordSuccess, setChangePasswordSuccess] = useState(false);

    const userApiService = new UserApiService();

    // Inline validation flags — derived from current inputs so they update as the user types.
    const passwordsMismatch = Boolean(newPassword) && Boolean(confirmNewPassword) && newPassword !== confirmNewPassword;
    const newPasswordEmpty = newPassword === '';

    const { run: changePassword, loading: saving } = useAsyncCallback(async () => {
        if (newPasswordEmpty) {
            setErrorMessage(t('userChangePassword.validation.required'));
            setChangePasswordSuccess(false);
            snackbar.error(t('userChangePassword.validation.required'));
            return;
        }

        if (passwordsMismatch || newPassword !== confirmNewPassword) {
            setErrorMessage(t('userChangePassword.validation.mustMatch'));
            setChangePasswordSuccess(false);
            snackbar.error(t('userChangePassword.validation.mustMatch'));
            return;
        }

        setErrorMessage('');
        const response = await userApiService.changeUserPassword(user.usId, newPassword);
        if (response && response.success) {
            snackbar.success(t('userChangePassword.toast.updated'));
            setNewPassword('');
            setConfirmNewPassword('');
            setChangePasswordSuccess(true);
        } else {
            snackbar.error(t('userChangePassword.error.unableToUpdate', { error: response.message }));
            setChangePasswordSuccess(false);
        }
    });

    return (
        <Box sx={{ pt: 1 }}>
            <Section title={t('userChangePassword.section.newPassword')}>
                <Grid item xs={12}>
                    <Stack spacing={1.5}>
                        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
                        {changePasswordSuccess && <Alert severity="success">{t('userChangePassword.success.changed')}</Alert>}
                    </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        type='password'
                        label={t('userChangePassword.field.newPassword')}
                        helperText={t('userChangePassword.helper.policy')}
                        value={newPassword}
                        onChange={(event) => setNewPassword(event.target.value)}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        type='password'
                        label={t('userChangePassword.field.confirmNewPassword')}
                        error={passwordsMismatch}
                        helperText={passwordsMismatch ? t('userChangePassword.validation.mustMatch') : ''}
                        value={confirmNewPassword}
                        onChange={(event) => setConfirmNewPassword(event.target.value)}
                    />
                </Grid>
                <Grid item xs={12}>
                    <SaveButton
                        loading={saving}
                        variant='contained'
                        onClick={changePassword}
                        disabled={newPasswordEmpty || passwordsMismatch}
                    >
                        {t('userChangePassword.button.changePassword')}
                    </SaveButton>
                </Grid>
            </Section>
        </Box>
    );
}

UserChangePassword.propTypes = {
    user: PropTypes.objectOf(UserAdminDto),
};
